import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { HC } from "./theme";
import { Card, SectionHead, Btn, Pill } from "./ui";
import { Sparkles, RefreshCw, AlertTriangle, TrendingUp, Users, Wallet, ArrowRight, Info } from "lucide-react";
import { useNavigate } from "react-router-dom";

const KIND = {
  action_required: { color: HC.red, tint: HC.redTint, Icon: AlertTriangle, label: "ACTION REQUIRED" },
  watch: { color: HC.amber, tint: HC.amberTint, Icon: Info, label: "WATCH" },
  positive: { color: HC.green, tint: HC.greenTint, Icon: TrendingUp, label: "POSITIVE TREND" },
  opportunity: { color: HC.purple, tint: HC.purpleTint, Icon: Users, label: "OPPORTUNITY" },
};

const AREA_ROUTE = { attendance: "/hr/attendance", overtime: "/hr/reports", payroll: "/hr/payroll", staffing: "/hr/shifts", leave: "/hr/leaves" };

export default function WorkforceIntelligence({ locationId }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const load = (refresh = 0) => {
    setBusy(true);
    api.get("/hr/intelligence", { params: { location_id: locationId || undefined, refresh } })
      .then(setData).catch(() => {}).finally(() => setBusy(false));
  };
  useEffect(() => { load(0); /* eslint-disable-next-line */ }, [locationId]);

  const dp = data?.data_period;

  return (
    <Card testid="hr-intelligence" className="relative overflow-hidden">
      <SectionHead
        title={<span className="flex items-center gap-2"><Sparkles size={17} style={{ color: HC.gold }} /> Workforce Intelligence</span>}
        sub={dp ? `Analysed ${dp.attendance_from} → ${dp.attendance_to} · ${data?.location_name}` : "Analysing workforce data…"}
        right={
          <div className="flex items-center gap-2">
            {data && <Pill status={data.source === "ai" ? "active" : "draft"} text={data.source === "ai" ? "AI" : "Rules"} testid="hr-intel-source" />}
            <Btn variant="outline" onClick={() => load(1)} disabled={busy} data-testid="hr-intel-refresh" className="h-9 px-3">
              <RefreshCw size={14} className={busy ? "animate-spin" : ""} /> Refresh
            </Btn>
          </div>
        }
      />

      {!data ? (
        <div className="h-24 rounded-xl animate-pulse" style={{ background: HC.borderSoft }} />
      ) : (
        <>
          <p className="text-sm leading-relaxed" style={{ color: HC.text }} data-testid="hr-workforce-brief">
            {data.workforce_brief}
          </p>

          {!!data.insights?.length && (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 mt-4">
              {data.insights.map((ins, i) => {
                const k = KIND[ins.kind] || KIND.watch;
                return (
                  <div key={i} data-testid={`hr-insight-${i}`} className="rounded-xl border p-4 flex flex-col"
                    style={{ borderColor: HC.border, background: HC.panelSoft }}>
                    <div className="flex items-center gap-2">
                      <span className="w-7 h-7 rounded-full flex items-center justify-center shrink-0" style={{ background: k.tint, color: k.color }}>
                        <k.Icon size={14} />
                      </span>
                      <span className="text-[9px] font-bold tracking-[0.16em]" style={{ color: k.color }}>{k.label}</span>
                    </div>
                    <div className="font-brand font-bold text-sm mt-2" style={{ color: HC.text }}>{ins.title}</div>
                    <div className="text-[12.5px] mt-1 leading-relaxed" style={{ color: HC.muted }}>{ins.body}</div>
                    {ins.why && <div className="text-[11.5px] mt-2 italic" style={{ color: HC.soft }}>{ins.why}</div>}
                    {ins.recommendation && (
                      <div className="text-[12px] mt-2 pl-2 border-l-2" style={{ borderColor: HC.gold, color: HC.text }}>
                        {ins.recommendation}
                      </div>
                    )}
                    {!!ins.metrics?.length && (
                      <div className="mt-3 space-y-0.5">
                        {ins.metrics.slice(0, 3).map((m, j) => (
                          <div key={j} className="text-[10.5px]" style={{ color: HC.soft }}>· {m}</div>
                        ))}
                      </div>
                    )}
                    <div className="flex-1" />
                    <button onClick={() => navigate(AREA_ROUTE[ins.area] || "/hr")}
                      data-testid={`hr-insight-cta-${i}`}
                      className="mt-3 inline-flex items-center gap-1 text-[12px] font-semibold" style={{ color: HC.burgundy }}>
                      View details <ArrowRight size={13} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            {!!data.risks?.length && (
              <div className="rounded-xl p-4 border" style={{ borderColor: HC.redTint, background: HC.redTint }}>
                <div className="text-[10px] font-bold tracking-[0.16em] mb-2" style={{ color: HC.red }}>RISKS</div>
                {data.risks.map((r, i) => <div key={i} className="text-[12.5px] mb-1" style={{ color: HC.text }}>· {r}</div>)}
              </div>
            )}
            {!!data.recommended_actions?.length && (
              <div className="rounded-xl p-4 border" style={{ borderColor: HC.border, background: HC.goldSoft }}>
                <div className="text-[10px] font-bold tracking-[0.16em] mb-2" style={{ color: HC.gold }}>RECOMMENDED ACTIONS (ADVISORY)</div>
                {data.recommended_actions.map((r, i) => <div key={i} className="text-[12.5px] mb-1" style={{ color: HC.text }}>{i + 1}. {r}</div>)}
              </div>
            )}
          </div>

          <div className="mt-3 flex items-center gap-2 text-[10.5px]" style={{ color: HC.soft }}>
            <Wallet size={12} /> AI is advisory only — it never calculates or approves payroll. Generated{" "}
            {data.generated_at ? new Date(data.generated_at).toLocaleString("en-NG") : "—"} · {data.model}
          </div>
        </>
      )}
    </Card>
  );
}
